import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Target, IndianRupee, Calendar, User, Building2,
  Pencil, X, Save, Plus, Sparkles, Copy, Calculator,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';
import { useTenantConfig } from '@/hooks/useTenantConfig';

interface Deal {
  id: string;
  title: string;
  value: number;
  probability?: number;
  stageId: string;
  expectedClose?: string;
  createdAt: string;
  updatedAt: string;
  contact?: { id: string; firstName: string; lastName: string } | null;
  account?: { id: string; name: string } | null;
  assignedTo?: { id: string; firstName: string; lastName: string } | null;
  stage?: { id: string; name: string; probability?: number; color?: string } | null;
  activities?: { id: string; type: string; title?: string; subject?: string; createdAt: string }[];
}

interface Stage { id: string; name: string; probability?: number; }
interface Contact { id: string; firstName: string; lastName: string; }
interface Account { id: string; name: string; }
interface Pipeline { id: string; name: string; stages: Stage[]; }
interface TenantUser { id: string; firstName: string; lastName: string; email: string; }

const probabilityColor = (prob?: number) => {
  if (prob == null) return 'bg-gray-100 text-gray-600';
  if (prob >= 75) return 'bg-green-100 text-green-700';
  if (prob >= 50) return 'bg-yellow-100 text-yellow-700';
  if (prob >= 25) return 'bg-orange-100 text-orange-700';
  return 'bg-red-100 text-red-700';
};

export function DealDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [deal, setDeal] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Track D — show "Open in Meeting Mode" only when the tenant has the
  // pool calculator module enabled. Module-flag-driven; never tenant-slug-driven.
  const { enabledModules } = useTenantConfig();
  const poolCalcEnabled =
    enabledModules === null || (Array.isArray(enabledModules) && enabledModules.includes('pool_heat_pump_calculator'));

  // Edit state
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stages, setStages] = useState<Stage[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [assigning, setAssigning] = useState(false);
  const [form, setForm] = useState({
    title: '', value: '', stageId: '', contactId: '', accountId: '',
    expectedCloseDate: '', probability: '', assignedToId: '',
  });

  // AI Features
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [proposalTone, setProposalTone] = useState('professional');
  const [draftingProposal, setDraftingProposal] = useState(false);
  const [proposalDraft, setProposalDraft] = useState<{ title: string; content: string } | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Add Activity state
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [activitySaving, setActivitySaving] = useState(false);
  const [activityForm, setActivityForm] = useState({
    type: 'note' as string,
    title: '',
    description: '',
  });

  const fetchDeal = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/deals/${id}`);
      const d = res.data.data;
      setDeal(d);
      setForm({
        title: d.title || '',
        value: String(d.value || ''),
        stageId: d.stageId || '',
        contactId: d.contact?.id || '',
        accountId: d.account?.id || '',
        expectedCloseDate: d.expectedClose ? d.expectedClose.split('T')[0] : '',
        probability: d.probability != null ? String(d.probability) : '',
        assignedToId: d.assignedTo?.id || '',
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load deal');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDeal();
    api.get('/deals/pipelines').then(res => {
      const pipelines: Pipeline[] = res.data.data?.pipelines ?? res.data.data ?? [];
      const allStages = pipelines.flatMap(p => p.stages || []);
      setStages(allStages);
    }).catch(() => {});
    api.get('/contacts').then(res => setContacts(res.data.data?.contacts ?? res.data.data ?? [])).catch(() => {});
    api.get('/accounts').then(res => setAccounts(res.data.data?.accounts ?? res.data.data ?? [])).catch(() => {});
    api.get('/auth/users').then(res => setUsers(res.data.data ?? [])).catch(() => {});
  }, [fetchDeal]);

  const handleAssign = async (newAssigneeId: string) => {
    if (!id) return;
    try {
      setAssigning(true);
      await api.put(`/deals/${id}`, { assignedToId: newAssigneeId || null });
      await fetchDeal();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to assign deal');
    } finally {
      setAssigning(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/deals/${id}`, {
        title: form.title,
        value: parseFloat(form.value) || 0,
        stageId: form.stageId || undefined,
        contactId: form.contactId || null,
        accountId: form.accountId || null,
        expectedClose: form.expectedCloseDate || undefined,
        probability: form.probability ? parseFloat(form.probability) : undefined,
        assignedToId: form.assignedToId || null,
      });
      setEditing(false);
      fetchDeal();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update deal');
    } finally {
      setSaving(false);
    }
  };

  const handleAddActivity = async () => {
    try {
      setActivitySaving(true);
      await api.post('/activities', {
        type: activityForm.type,
        title: activityForm.title || undefined,
        description: activityForm.description || undefined,
        dealId: id,
        contactId: deal?.contact?.id || undefined,
        accountId: deal?.account?.id || undefined,
      });
      setShowActivityForm(false);
      setActivityForm({ type: 'note', title: '', description: '' });
      fetchDeal();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to add activity');
    } finally {
      setActivitySaving(false);
    }
  };

  const handleDraftProposal = async () => {
    if (!id) return;
    try {
      setDraftingProposal(true);
      const res = await api.post('/ai/proposal-draft', { dealId: id, tone: proposalTone });
      const data = res.data.data ?? res.data;
      setProposalDraft({ title: data.title, content: data.content });
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to draft proposal. You may not have enough AI credits.');
    } finally {
      setDraftingProposal(false);
    }
  };

  const handleGetSuggestions = async () => {
    if (!id) return;
    try {
      setLoadingSuggestions(true);
      const res = await api.get(`/ai/suggestions/deal/${id}`);
      const data = res.data.data ?? res.data;
      setSuggestions(data.suggestions ?? []);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to get suggestions. You may not have enough AI credits.');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-gray-500">Loading deal...</span>
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-red-500">
        <p className="text-sm">{error || 'Deal not found'}</p>
        <button onClick={() => navigate('/deals')} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Back to Deals</button>
      </div>
    );
  }

  const inp = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]';
  const lbl = 'mb-1 block text-sm font-medium text-gray-700';

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <button onClick={() => navigate('/deals')} className="rounded-lg border border-gray-300 p-2 text-gray-500 hover:bg-gray-50">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{deal.title}</h1>
          <p className="text-sm text-gray-500">Deal created {formatDate(deal.createdAt)}</p>
        </div>
        <div className="flex items-center gap-2">
          {poolCalcEnabled && (
            <button
              onClick={() => navigate(`/sales/meeting/start?leadId=${deal.id}`)}
              className="flex items-center gap-1.5 rounded-lg border border-[#0066b3] bg-[#f3f8ff] px-3 py-2 text-sm font-medium text-[#0066b3] hover:bg-[#e1edfa] transition-colors"
              title="Open the Sun Stellar pool heat pump calculator with this lead pre-bound"
            >
              <Calculator size={14} />
              Open in Meeting Mode
            </button>
          )}
          <button
            onClick={() => { setShowProposalModal(true); setProposalDraft(null); }}
            className="flex items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700 transition-colors"
            title="AI Proposal Draft (5 credits)"
          >
            <Sparkles size={14} />
            AI Proposal
          </button>
          <button
            onClick={handleGetSuggestions}
            disabled={loadingSuggestions}
            className="flex items-center gap-1.5 rounded-lg border border-purple-500/50 bg-purple-500/10 px-3 py-2 text-sm font-medium text-purple-600 hover:bg-purple-500/20 disabled:opacity-50 transition-colors"
            title="Get AI Suggestions (1 credit)"
          >
            {loadingSuggestions ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Suggestions
          </button>
          <button
            onClick={() => setEditing(!editing)}
            className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Pencil size={14} />
            {editing ? 'Cancel' : 'Edit'}
          </button>
        </div>
      </div>

      {/* AI Suggestions */}
      {suggestions.length > 0 && (
        <div className="mb-6 rounded-lg border border-indigo-500/20 bg-indigo-500/5 p-3">
          <p className="mb-2 text-xs font-semibold text-indigo-600 uppercase tracking-wider flex items-center gap-1">
            <Sparkles size={12} /> AI Suggestions
          </p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s, i) => (
              <span key={i} className="inline-flex rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-1.5 text-xs text-[var(--text-secondary)]">
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Deal Header Card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            {editing ? (
              <div className="space-y-4">
                <div>
                  <label className={lbl}>Title *</label>
                  <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className={inp} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>Value (INR) *</label>
                    <input type="number" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} className={inp} />
                  </div>
                  <div>
                    <label className={lbl}>Stage</label>
                    <select value={form.stageId} onChange={e => setForm({ ...form, stageId: e.target.value })} className={inp}>
                      <option value="">Select Stage</option>
                      {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>Probability (%)</label>
                    <input type="number" value={form.probability} onChange={e => setForm({ ...form, probability: e.target.value })} min="0" max="100" className={inp} />
                  </div>
                  <div>
                    <label className={lbl}>Expected Close Date</label>
                    <input type="date" value={form.expectedCloseDate} onChange={e => setForm({ ...form, expectedCloseDate: e.target.value })} className={inp} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>Contact</label>
                    <select value={form.contactId} onChange={e => setForm({ ...form, contactId: e.target.value })} className={inp}>
                      <option value="">None</option>
                      {contacts.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={lbl}>Account</label>
                    <select value={form.accountId} onChange={e => setForm({ ...form, accountId: e.target.value })} className={inp}>
                      <option value="">None</option>
                      {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className={lbl}>Assigned To</label>
                  <select value={form.assignedToId} onChange={e => setForm({ ...form, assignedToId: e.target.value })} className={inp}>
                    <option value="">— Unassigned —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
                  </select>
                </div>
                <div className="flex justify-end">
                  <button onClick={handleSave} disabled={saving || !form.title || !form.value}
                    className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--saffron-50)]">
                    <IndianRupee size={20} className="text-[var(--ink-0)]" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Deal Value</p>
                    <p className="text-lg font-bold text-gray-900">{formatINR(deal.value)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                    <Target size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Stage</p>
                    <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                      {deal.stage?.name || '-'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-50">
                    <Calendar size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Probability</p>
                    {deal.probability != null ? (
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${probabilityColor(deal.probability)}`}>
                        {deal.probability}%
                      </span>
                    ) : <p className="text-sm text-gray-900">-</p>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-50">
                    <Calendar size={20} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Expected Close</p>
                    <p className="text-sm text-gray-900">{deal.expectedClose ? formatDate(deal.expectedClose) : '-'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Activity Timeline */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Activity</h2>
              <button
                onClick={() => setShowActivityForm(!showActivityForm)}
                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {showActivityForm ? <X size={14} /> : <Plus size={14} />}
                {showActivityForm ? 'Cancel' : 'Add Activity'}
              </button>
            </div>

            {showActivityForm && (
              <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">Type *</label>
                    <select
                      value={activityForm.type}
                      onChange={e => setActivityForm({ ...activityForm, type: e.target.value })}
                      className={inp}
                    >
                      <option value="note">Note</option>
                      <option value="call">Call</option>
                      <option value="meeting">Meeting</option>
                      <option value="visit">Visit / Site Visit</option>
                      <option value="email">Email</option>
                      <option value="task">Task</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">Title</label>
                    <input
                      type="text"
                      value={activityForm.title}
                      onChange={e => setActivityForm({ ...activityForm, title: e.target.value })}
                      placeholder="Activity title"
                      className={inp}
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">Description</label>
                  <textarea
                    value={activityForm.description}
                    onChange={e => setActivityForm({ ...activityForm, description: e.target.value })}
                    placeholder="Add notes or details..."
                    rows={2}
                    className={inp}
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={handleAddActivity}
                    disabled={activitySaving}
                    className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                  >
                    {activitySaving && <Loader2 size={14} className="animate-spin" />}
                    Save Activity
                  </button>
                </div>
              </div>
            )}

            {deal.activities && deal.activities.length > 0 ? (
              <div className="space-y-3">
                {deal.activities.map(act => (
                  <div key={act.id} className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[var(--saffron-50)] text-xs font-medium text-[var(--ink-0)]">
                      {act.type?.[0]?.toUpperCase() || 'A'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{act.title || act.subject || 'Activity'}</p>
                      <p className="text-xs text-gray-500">{formatDate(act.createdAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No activities recorded yet.</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact & Account */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h3 className="mb-4 text-sm font-semibold text-gray-900">Contact & Account</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <User size={16} className="text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Contact</p>
                  {deal.contact ? (
                    <button onClick={() => navigate(`/contacts/${deal.contact!.id}`)} className="text-sm font-medium text-[var(--ink-0)] hover:underline">
                      {deal.contact.firstName} {deal.contact.lastName}
                    </button>
                  ) : <p className="text-sm text-gray-400">-</p>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Building2 size={16} className="text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Account</p>
                  {deal.account ? (
                    <button onClick={() => navigate(`/accounts/${deal.account!.id}`)} className="text-sm font-medium text-[var(--ink-0)] hover:underline">
                      {deal.account.name}
                    </button>
                  ) : <p className="text-sm text-gray-400">-</p>}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <User size={16} className="mt-1 text-gray-400" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Assigned To</p>
                  <div className="mt-1 flex items-center gap-2">
                    <select
                      value={deal.assignedTo?.id || ''}
                      onChange={(e) => handleAssign(e.target.value)}
                      disabled={assigning}
                      className="flex-1 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)] disabled:opacity-50"
                    >
                      <option value="">— Unassigned —</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.firstName} {u.lastName}
                        </option>
                      ))}
                    </select>
                    {assigning && <Loader2 size={14} className="animate-spin text-[var(--saffron-600)]" />}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Meta */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h3 className="mb-4 text-sm font-semibold text-gray-900">Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span className="text-gray-900">{formatDate(deal.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Updated</span>
                <span className="text-gray-900">{formatDate(deal.updatedAt)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Proposal Draft Modal */}
      {showProposalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowProposalModal(false)}>
          <div className="mx-4 w-full max-w-lg rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[var(--border-primary)] px-6 py-4 sticky top-0 z-10 bg-[var(--bg-surface)] rounded-t-xl">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-[var(--text-primary)]">
                <Sparkles size={18} className="text-purple-500" />
                AI Proposal Draft
                <span className="text-xs font-normal text-[var(--text-muted)]">(5 credits)</span>
              </h2>
              <button onClick={() => setShowProposalModal(false)} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="mb-1 block text-sm font-medium text-[var(--text-secondary)]">Tone</label>
                  <select
                    value={proposalTone}
                    onChange={(e) => setProposalTone(e.target.value)}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  >
                    <option value="formal">Formal</option>
                    <option value="friendly">Friendly</option>
                    <option value="professional">Professional</option>
                    <option value="concise">Concise</option>
                  </select>
                </div>
                <button
                  onClick={handleDraftProposal}
                  disabled={draftingProposal}
                  className="flex items-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  {draftingProposal ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  Generate
                </button>
              </div>
              {proposalDraft && (
                <div className="space-y-3">
                  <div>
                    <div className="mb-1 flex items-center justify-between">
                      <label className="text-xs font-medium text-[var(--text-muted)]">Title</label>
                      <button onClick={() => copyToClipboard(proposalDraft.title)} className="text-xs text-purple-500 hover:text-purple-600 flex items-center gap-1">
                        <Copy size={10} /> Copy
                      </button>
                    </div>
                    <div className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm font-medium text-[var(--text-primary)]">
                      {proposalDraft.title}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 flex items-center justify-between">
                      <label className="text-xs font-medium text-[var(--text-muted)]">Content</label>
                      <button onClick={() => copyToClipboard(proposalDraft.content)} className="text-xs text-purple-500 hover:text-purple-600 flex items-center gap-1">
                        <Copy size={10} /> Copy
                      </button>
                    </div>
                    <textarea
                      readOnly
                      value={proposalDraft.content}
                      rows={10}
                      className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] px-3 py-2 text-sm text-[var(--text-primary)] resize-y"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end border-t border-[var(--border-primary)] px-6 py-4">
              <button
                onClick={() => setShowProposalModal(false)}
                className="rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
